import type { Card } from "@amzn/ads-blink-client";
import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import type { AsyncStatus } from "@react-hookz/web";
import { useDebouncedEffect } from "@react-hookz/web";
import { useCallback, useEffect, useRef, useState } from "react";
import * as uuid from "uuid";
import { useStore } from "zustand";

import { getDiscoveryApiClient } from "../../api/discoveryApi/discoveryApi";
import { getPublisherPortalApiClient } from "../../api/publisherPortalApi/publisherPortalApi";
import { ContentSearchErrorNotificationMessage } from "../../components/common/notification.components";
import type { UnifiedFiltersOptions } from "../../config/filters/unified";
import { unifiedFilters } from "../../config/filters/unified";
import { allPackagedInsightScope } from "../../constants/filters";
import { FeatureFlags, FeatureFlagsStore } from "../../store/featureFlags";
import { discoveryFilterStore } from "../../store/filters";
import { notificationStore, NotificationType } from "../../store/notification";
import { unifiedFilterStore } from "../../store/unifiedFilters";
import { ContentPreviewType } from "../../types/content";
import type { BaseFilterOptionModel } from "../../types/filters/common";
import {
    CoreUnifiedFilterKeys,
    UnifiedFilterKeys,
} from "../../types/filters/unified";
import { IHubContentProvider } from "../../types/providers";
import { isLoading, isSuccess } from "../../utils/api.utils";
import type { FilterOptionsMap } from "../../utils/filters.utils";
import {
    getFilterOptions,
    getPreselectedLanguages,
} from "../../utils/filters.utils";
import {
    getAdvertiserId,
    getMarketplaceId,
    isAccountAgnosticId,
} from "../../utils/permission.utils";
import { logInsightBrowseResults } from "../../utils/telemetry.utils";
import {
    getInsightIdFromSearchParams,
    isMarketingContentFromSearchParams,
} from "../../utils/window.utils";
import { useGeneratedInsightsAction } from "../generatedInsights/useGeneratedInsightsAction";
import { useSingleMarketingNarrativeAction } from "../marketingContent/useSingleMarketingContentLoader";

import {
    DEFAULT_PAGE_SIZE,
    useGetUnifiedContentsAsync,
} from "./useUnifiedContentsAction";
import { useGetUnifiedContentsFiltersAsync } from "./useUnifiedContentsFiltersAction";
import { useGetUnifiedContentsFiltersV2Async } from "./useUnifiedContentsFiltersActionV2";

export interface UnifiedContentsLoaderProps {
    isLoaderInView?: boolean;
}

/**
 * This loader was added as part of the unified search experience project, and is intended to be the sole content loader for
 * all content types that fall under the scope of that search. As of 4/9/2025, this only supports Packaged Insights due to
 * the incremental rollout of unified search, and as such certain values are hard-coded for this use-case until more progress
 * is made in other systems to support dynamic values.
 */
export function useUnifiedContentsLoader({
    isLoaderInView,
}: UnifiedContentsLoaderProps): [AsyncStatus, Card[], number] {
    const [contents, setContents] = useState<Card[]>([]);
    const [filters, setFilters] = useState<UnifiedFiltersOptions>();
    const [contentCount, setContentCount] = useState<number>(0);
    const [shouldAutoSearch, setShouldAutoSearch] = useState(false);
    const pageNumber = useRef(1);

    const [useUnifiedSearch, uatPackagedInsights] = useStore(
        FeatureFlagsStore,
        (state) => [state.unifiedSearch, state.uatPackagedInsights],
    );

    const [
        setFilterOptions,
        setData,
        fieldSelections,
        contentSubType,
        searchQuery,
        setTypeOptions,
    ] = useStore(discoveryFilterStore, (state) => [
        state.setFilterOptions,
        state.setData,
        state.getInsightsFilters().UNIFIED.fieldSelections,
        state.getContentSubType(),
        state.getInsightsFilters()[IHubContentProvider.UNIFIED].searchQuery,
        state.setTypeOptions,
    ]);

    const [
        unifiedFiltersV2,
        searchQueryV2,
        filterContentStateIsDirty,
        filterMetadataStateIsDirty,
        setEnabledTypes,
        setUnifiedFilters,
        setIsContentDirty,
        setIsMetadataDirty,
        setLastSearchParameters,
        setLastSearchResultTotalCount,
    ] = useStore(unifiedFilterStore, (state) => [
        state.filters,
        state.searchQuery,
        state.isContentDirty,
        state.isMetadataDirty,
        state.setEnabledTypes,
        state.setFilters,
        state.setIsContentDirty,
        state.setIsMetadataDirty,
        state.setLastSearchParameters,
        state.setLastSearchResultTotalCount,
    ]);

    const searchId = useRef<string>(uuid.v4());
    const searchErrors = useRef<string[]>([]);
    const [upsertNotification, dismissNotification] = useStore(
        notificationStore,
        (state) => [state.upsertNotification, state.dismissNotification],
    );

    const previousFieldSelection = useRef(fieldSelections);

    const searchSFid = isAccountAgnosticId() ? undefined : getAdvertiserId()!;
    const marketplaceId = getMarketplaceId()!;

    const [getContentsState, getContentsAction] = useGetUnifiedContentsAsync({
        advertiserId: searchSFid,
        marketplaceId,
    });

    const [getFiltersStateV2, getFiltersActionV2] =
        useGetUnifiedContentsFiltersV2Async({
            advertiserId: searchSFid,
            marketplaceId,
        });

    const [getFiltersState, getFiltersAction] =
        useGetUnifiedContentsFiltersAsync({
            advertiserId: searchSFid,
            marketplaceId,
        });

    const [generatedInsightsState, getneratedInsightsAction] =
        useGeneratedInsightsAction();

    const [singleMarketingContentState] = useSingleMarketingNarrativeAction();

    const insightIdentifier = getInsightIdFromSearchParams();
    const enableSingleIdentifierView = !!insightIdentifier;

    // Determine if we are viewing marketing content in single identifier view
    const isMarketingContentInSingleView =
        enableSingleIdentifierView && isMarketingContentFromSearchParams();

    const contentRequestID = useRef(uuid.v4());
    /**
     * Performs all of the logic needed to retrieve the insights from the the api
     */
    const fetchContents = useCallback(
        async (isPagination = false) => {
            const newId = uuid.v4();
            contentRequestID.current = newId;

            // Until we can implement the event-drive approach for packaged insights (ETA Q3'25), we need a seperate flow for
            // retrieving UAT packaged insights. This is some gross hard-coded logic to force the temporary experience.
            if (useUnifiedSearch && uatPackagedInsights) {
                getContentsState.status = "not-executed";
                setContents([]);

                // Stop the scroll from causing another reload
                pageNumber.current = 999;

                const { data: templates = [] } = await (
                    await getPublisherPortalApiClient()
                )
                    .getTemplatesWithAssociations({})
                    .catch((e) => {
                        searchErrors.current = [
                            ...searchErrors.current,
                            e.message,
                        ];
                        return { data: [] };
                    });

                const selectedThemes = unifiedFiltersV2.UNIFIED_TYPE.filter(
                    (item) => item.isSelected,
                );
                const asCard = templates
                    // Filter based on selected types
                    .filter(
                        (t) =>
                            selectedThemes.length === 0 ||
                            selectedThemes.some((s) =>
                                t.program?.themes
                                    ?.map((t) => t.toLowerCase())
                                    .includes(s.label.toLowerCase()),
                            ),
                    )
                    // Filter based on search Query
                    .filter(
                        (t) =>
                            searchQueryV2.trim().length === 0 ||
                            t.name
                                ?.toLowerCase()
                                .includes(searchQueryV2.toLowerCase()),
                    )
                    // Convert to the unified card layout so it can render in the unified viewer
                    .map((t) => {
                        return {
                            identifier: t.id,
                            insightType: InsightType.CDB_TEMPLATE,
                            details: {
                                domainId: t.domain?.id,
                            },
                            header: t.program?.themes?.join(","),
                            title: t.name,
                            subtitle: t.program?.name,
                            footer: t.language,
                            description: t.description,
                            cardHeader: t.name,
                            additionalInfo: {
                                topic: t.program?.themes?.join(","),
                                language: t.language,
                            },
                            lastModifiedDate: t.domain?.releaseTimestamp,
                        } as Card;
                    });

                // We only render the content count if the status is success, so force it here
                getContentsState.status = "success";
                setContentCount(asCard.length);
                setContents(asCard);
                return;
            }

            let curInsights = contents;
            if (!isPagination) {
                setContents([]);
                curInsights = [];
                pageNumber.current = 1;
            }

            // Set last search before API response. If we error out, times out, etc we still want to indicate that
            // we tried a search with the selected parameters already
            setLastSearchParameters();
            await getContentsAction
                .execute(pageNumber.current)
                .then(({ content, totalHits, errors }) => {
                    if (contentRequestID.current !== newId && useUnifiedSearch)
                        return;

                    // Push our errors to the error list
                    const additionalErrors = errors.map(
                        (e) => `[${e.provider}]: ${e.errorMessage}`,
                    );
                    searchErrors.current = [
                        ...searchErrors.current,
                        ...additionalErrors,
                    ];

                    setContentCount(totalHits);
                    setLastSearchResultTotalCount(totalHits);

                    setData(IHubContentProvider.UNIFIED, [
                        ...curInsights,
                        ...content,
                    ]);
                    setContents([...curInsights, ...content]);
                    pageNumber.current = pageNumber.current + 1;
                })
                .catch((e) => {
                    searchErrors.current = [...searchErrors.current, e.message];
                });
        },
        [
            searchQuery,
            searchQueryV2,
            fieldSelections,
            unifiedFiltersV2,
            pageNumber,
            contents,
        ],
    );

    // This just needs to be anything that doesn't appear in an insight id
    const filterDelim = "|||";
    const sortAndCleanFilters = (filters: BaseFilterOptionModel[]) => {
        return (
            filters
                .filter(
                    (val, i, arr) =>
                        i === arr.findIndex((val2) => val2.label === val.label),
                )
                // We currently have options being returned with the same id but different labels, which causes the
                // multi-selects to select both options since their ids match. For now we can just overwrite the id with the label
                // But long-term we need to fix the underlying data source from the providers
                .map((val) => {
                    const newId = val.id.includes(filterDelim)
                        ? val.id
                        : `${val.id}${filterDelim}${val.label}`;
                    return { ...val, id: newId };
                })
                .sort((a, b) => a.label.localeCompare(b.label))
        );
    };

    const filterRequestID = useRef(uuid.v4());
    const fetchFiltersV2 = useCallback(async () => {
        if (!useUnifiedSearch) {
            return;
        }

        // Until we can implement the event-drive approach for packaged insights (ETA Q3'25), we need a seperate flow for
        // retrieving UAT packaged insights. This is some gross hard-coded logic to force the temporary experience.
        if (uatPackagedInsights) {
            // Only enable the UAT types
            const uatTypes = unifiedFiltersV2[CoreUnifiedFilterKeys.TYPE]
                .filter(
                    (t) =>
                        t.enablingFeatureFlag ===
                        FeatureFlags.UAT_PACKAGED_INSIGHT,
                )
                .map((t) => t.id);
            setEnabledTypes(uatTypes);

            // Remove any filter pills options
            Object.values(UnifiedFilterKeys).forEach((key) =>
                setUnifiedFilters(key, []),
            );
            return;
        }

        const newId = uuid.v4();
        filterRequestID.current = newId;

        // Update the type dropdown
        const typePromise = getFiltersActionV2
            .execute({ ignoreScope: true })
            .then(({ scopes }) => {
                if (filterRequestID.current !== newId) return;
                setEnabledTypes(scopes);
            })
            .catch((e) => {
                searchErrors.current = [...searchErrors.current, e.message];
            });
        if (filterRequestID.current !== newId) return;

        const hasSelectedValues: Set<UnifiedFilterKeys> = new Set();
        const noSelectedValues: Set<UnifiedFilterKeys> = new Set();

        const selectedOptionsMap: Record<string, any[]> = {};

        // Sort our filters into selected vs not-selected
        Object.entries(unifiedFiltersV2)
            .filter(([key, _]) =>
                Object.values(UnifiedFilterKeys).includes(
                    key as UnifiedFilterKeys,
                ),
            )
            .forEach(([key, value]) => {
                const selectedOptions =
                    (
                        value as { isSelected: boolean; isDisabled: boolean }[]
                    )?.filter(
                        (model) => model.isSelected && !model.isDisabled,
                    ) ?? [];

                const hasSelected = selectedOptions.length > 0;
                if (hasSelected) {
                    hasSelectedValues.add(key as UnifiedFilterKeys);
                    selectedOptionsMap[key] = selectedOptions;
                } else {
                    noSelectedValues.add(key as UnifiedFilterKeys);
                }
            });

        // Get the filters for dropdowns that aren't selected
        const notSelectedPromise = getFiltersActionV2
            .execute({})
            .then(({ filters }) => {
                if (filterRequestID.current !== newId) return;
                noSelectedValues.forEach((key) => {
                    const sorted = sortAndCleanFilters(filters[key] ?? []);

                    // Preselect languages based on selected SF marketplace
                    if (key === UnifiedFilterKeys.LANGUAGE) {
                        const preselectedLanguages = getPreselectedLanguages();
                        const preselectLabels = preselectedLanguages.map(
                            (lang) => lang.label,
                        );
                        let hasPreselections = false;

                        sorted.forEach((option) => {
                            if (preselectLabels.includes(option.label)) {
                                option.isSelected = true;
                                hasPreselections = true;
                            }
                        });

                        setUnifiedFilters(key as UnifiedFilterKeys, sorted);

                        if (hasPreselections) {
                            setLastSearchParameters();
                            setShouldAutoSearch(true);
                        }
                    } else {
                        setUnifiedFilters(key as UnifiedFilterKeys, sorted);
                    }
                });
            })
            .catch((e) => {
                searchErrors.current = [...searchErrors.current, e.message];
            });
        if (filterRequestID.current !== newId) return;

        // Get filters for the dropdowns that are selected. We have to request these individually, pretending no options are selected
        // so that we can see all potential options to add in
        const selectedPromises = Array.from(hasSelectedValues).map((key) => {
            getFiltersActionV2
                .execute({ ignoreField: key })
                .then(({ filters }) => {
                    if (filterRequestID.current !== newId) return;
                    const selected = selectedOptionsMap[key] ?? [];

                    // Force add in the selected values, and then remove any duplicates that caused
                    const newFilters = sortAndCleanFilters([
                        ...selected,
                        ...(filters[key] ?? []),
                    ]);
                    setUnifiedFilters(key as UnifiedFilterKeys, newFilters);
                })
                .catch((e) => {
                    searchErrors.current = [...searchErrors.current, e.message];
                });
        });

        await Promise.all([
            typePromise,
            notSelectedPromise,
            ...selectedPromises,
        ]);
    }, [searchQueryV2, unifiedFiltersV2]);

    /**
     * Performs all of the logic needed to retrieve the filters from the the api
     */
    const fetchFilters = useCallback(async () => {
        if (useUnifiedSearch) {
            return;
        }

        const filterResponse = await getFiltersAction.execute({}).catch((e) => {
            searchErrors.current = [...searchErrors.current, e.message];
            return {
                filters: {} as UnifiedFiltersOptions,
            };
        });

        const scopeOptions = (
            await getFiltersAction.execute({
                ignoreScope: true,
            })
        ).scopes;

        // This is used in TypeFilterButton.tsx to determine if a value in the type dropdown should be grayed out or not
        setTypeOptions(IHubContentProvider.UNIFIED, [
            allPackagedInsightScope.value!,
            ...scopeOptions,
        ]);

        const newFilters: UnifiedFiltersOptions = {
            ...filterResponse.filters,
        };

        // For any field that has a selected value, we will want to do a query where we are ignoring that selected value
        // This results in a list of all possible options that would INCREASE the number of results shown
        await Promise.all(
            unifiedFilters
                .filter((filter) => fieldSelections[filter.field]?.length !== 0)
                .map(async (filter) => {
                    await getFiltersAction
                        .execute({
                            ignoreField: filter.field,
                        })
                        .then(({ filters }) => {
                            newFilters[filter.field] = filters[filter.field];
                        })
                        .catch((e) => {
                            searchErrors.current = [
                                ...searchErrors.current,
                                e.message,
                            ];
                        });
                }),
        );

        setFilters(newFilters);
    }, [searchQuery, fieldSelections]);

    /**
     * Update the error message shown to the user when we get a new error
     */
    useEffect(() => {
        if (searchErrors.current.length) {
            upsertNotification(
                <ContentSearchErrorNotificationMessage
                    errors={searchErrors.current}
                />,
                NotificationType.ERROR,
                searchId.current,
            );
        } else {
            dismissNotification(searchId.current);
            searchId.current = uuid.v4();
        }
    }, [searchErrors.current]);

    /**
     * If we have an insight indentifier in the URL, then load just that singular insight
     */
    useEffect(() => {
        if (insightIdentifier) {
            pageNumber.current = 1;

            if (isMarketingContentInSingleView) {
                // Use Discovery API for marketing content in single identiefier view - call directly to get full response
                void (async () => {
                    const response = await (await getDiscoveryApiClient())
                        .getContentDetails(
                            insightIdentifier,
                            ContentPreviewType.ORIGINAL,
                            true,
                        )
                        .catch((e) => {
                            const errorMessage =
                                e instanceof Error ? e.message : String(e);
                            searchErrors.current = [
                                ...searchErrors.current,
                                errorMessage,
                            ];
                            return null;
                        });

                    if (response) {
                        // Transform Discovery API response to Card format
                        const content = response.content;
                        const contentUsers = response.contentUsers ?? [];

                        const unifiedContent: Card[] = content
                            ? [
                                  {
                                      identifier:
                                          content.contentId ??
                                          insightIdentifier,
                                      insightType: InsightType.MARKETING,
                                      details: {
                                          metadata: {
                                              ...content,
                                              contentUsers,
                                          } as any,
                                      },
                                      header: "",
                                      title: content.contentTitle ?? "",
                                      subtitle: "",
                                      footer: "",
                                      description:
                                          content.shortDescription ?? "",
                                  },
                              ]
                            : [];

                        setData(IHubContentProvider.UNIFIED, unifiedContent);
                        setContents(unifiedContent);
                        setContentCount(unifiedContent.length);
                    }
                })();
            } else {
                // Use existing Cerebro flow for all other content
                void getContentsAction
                    .execute(pageNumber.current, insightIdentifier)
                    .then(({ content, totalHits, errors }) => {
                        // Push our errors to the error list
                        const additionalErrors = errors.map(
                            (e) => `[${e.provider}]: ${e.errorMessage}`,
                        );
                        searchErrors.current = [
                            ...searchErrors.current,
                            ...additionalErrors,
                        ];

                        setData(IHubContentProvider.UNIFIED, content);
                        setContents(content);
                        setContentCount(totalHits);
                    })
                    .catch((e) => {
                        searchErrors.current = [
                            ...searchErrors.current,
                            e.message,
                        ];
                    });
            }
        }
    }, [insightIdentifier, isMarketingContentInSingleView]);

    /**
     * Update our rendered list of filters when:
     * 1. filters - There is a change in the filter values returned from the api
     */
    useEffect(() => {
        filters &&
            unifiedFilters.forEach((filter) => {
                const selectedFilters: FilterOptionsMap = fieldSelections[
                    filter.field
                ]?.reduce((acc, item) => {
                    acc[item.value.toString()] = item;
                    return acc;
                }, {} as FilterOptionsMap);

                const filterOptions = [
                    ...getFilterOptions({
                        ...selectedFilters,
                        ...filters[filter.field],
                    }).filter(
                        (val, i, arr) =>
                            i ===
                            arr.findIndex((val2) => val2.label === val.label),
                    ),
                ];

                setFilterOptions(filter.field, filterOptions);
            });

        previousFieldSelection.current = fieldSelections;
    }, [filters]);

    /**
     * Trigger a refresh of the data when the user scrolls to the bottom of the page, max 1 per second
     */
    useDebouncedEffect(
        () => {
            if (
                isLoaderInView &&
                !isLoading(getContentsState.status) &&
                pageNumber.current <=
                    Math.ceil(contentCount / DEFAULT_PAGE_SIZE) &&
                !enableSingleIdentifierView
            ) {
                searchErrors.current = [];
                void fetchContents(true);
            }
        },
        [
            isLoaderInView,
            contentCount,
            pageNumber,
            DEFAULT_PAGE_SIZE,
            enableSingleIdentifierView,
        ],
        500,
    );

    useEffect(() => {
        if (filterMetadataStateIsDirty) {
            searchErrors.current = [];
            void fetchFiltersV2().then(() => setIsMetadataDirty(false));
        }
    }, [filterMetadataStateIsDirty]);

    useEffect(() => {
        if (shouldAutoSearch && !enableSingleIdentifierView) {
            void fetchContents(false).then(() => setIsContentDirty(false));
            setShouldAutoSearch(false);
        }
    }, [
        unifiedFiltersV2[UnifiedFilterKeys.LANGUAGE],
        shouldAutoSearch,
        enableSingleIdentifierView,
    ]);

    // When we load this component, dirty our filter state
    useEffect(() => {
        setIsContentDirty(true);
        setIsMetadataDirty(true);
    }, []);

    /**
     * Trigger a refresh of the insights after 500ms of inactivity when:
     * 1. searchQuery - The user has changed the term they're searching for
     * 2. fieldSelections - The user has changed the filters they want to apply
     * 3. contentSubType - The user selected a new insight type
     */
    useDebouncedEffect(
        () => {
            const hasChanges = !useUnifiedSearch || filterContentStateIsDirty;
            if (!enableSingleIdentifierView && hasChanges) {
                searchErrors.current = [];
                void fetchContents(false).then(() => setIsContentDirty(false));

                if (useUnifiedSearch) {
                    setIsMetadataDirty(true);
                } else {
                    void fetchFilters();
                }
            }
        },
        [
            searchQuery,
            filterContentStateIsDirty,
            fieldSelections,
            contentSubType,
            enableSingleIdentifierView,
        ],
        500,
    );

    /**
     * Log data each time we retrieve insights
     */
    useDebouncedEffect(
        () => {
            if (isSuccess(getContentsState.status)) {
                setData(IHubContentProvider.UNIFIED, contents);
                logInsightBrowseResults(contents.length, useUnifiedSearch);
            }
        },
        [contents, getContentsState.status],
        500,
    );

    if (enableSingleIdentifierView) {
        // Use marketing content state for marketing content in single identifier view, otherwise use Cerebro state
        const stateToUse = isMarketingContentInSingleView
            ? singleMarketingContentState.status
            : getContentsState.status;
        return [stateToUse, contents, contentCount];
    } else {
        return [getContentsState.status, contents, contentCount];
    }
}
